import React from "react";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiCheckCircle,
  FiExternalLink,
  FiInfo,
  FiSettings,
  FiX,
} from "react-icons/fi";
import { getAnnouncementTypeClasses } from "../../utils/announcementUtils";

const typeIconMap = {
  info: FiInfo,
  success: FiCheckCircle,
  warning: FiAlertTriangle,
  error: FiAlertCircle,
  maintenance: FiSettings,
};

const AnnouncementPopup = ({
  announcement,
  loading = false,
  onDismiss,
  onAcknowledge,
  onNavigate,
}) => {
  if (!announcement) return null;

  const type = String(announcement.type || "info").toLowerCase();
  const Icon = typeIconMap[type] || FiInfo;
  const style = getAnnouncementTypeClasses(type);
  const hasCta =
    String(announcement.cta?.label || "").trim() &&
    String(announcement.cta?.url || "").trim();
  const dismissible = Boolean(announcement.dismissible);
  const requiresAcknowledgement = Boolean(announcement.requiresAcknowledgement);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-slate-950/55 p-3 backdrop-blur-sm sm:p-8">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-popup-title"
        className={`relative flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border shadow-2xl sm:max-h-[calc(100dvh-4rem)] sm:rounded-3xl ${style.panel} dark:border-gray-700 dark:bg-gray-900`}
      >
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#00ADB5] via-[#FFD369] to-[#F97316]" />

        <div className="flex shrink-0 items-start gap-3 border-b border-slate-200/80 px-4 pb-4 pt-5 dark:border-gray-700 sm:gap-4 sm:px-8 sm:pb-5 sm:pt-7">
          <div
            className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border sm:h-14 sm:w-14 sm:rounded-2xl ${style.accent}`}
          >
            <Icon size={22} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white dark:bg-slate-100 dark:text-slate-900 sm:px-3 sm:text-[11px] sm:tracking-[0.2em]">
                {type}
              </span>
              {requiresAcknowledgement && (
                <span className="rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-medium text-rose-700 sm:px-3 sm:text-xs">
                  รับทราบก่อนใช้งาน
                </span>
              )}
            </div>
            <h2
              id="announcement-popup-title"
              className="text-lg font-semibold leading-tight text-slate-900 dark:text-white sm:text-2xl"
            >
              {announcement.title || "System announcement"}
            </h2>
          </div>

          {dismissible && (
            <button
              type="button"
              onClick={onDismiss}
              disabled={loading}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/80 text-slate-500 transition hover:bg-white hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-gray-800/90 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white sm:h-10 sm:w-10"
              aria-label="Dismiss announcement"
              title="Dismiss announcement"
            >
              <FiX size={18} />
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-8 sm:py-6">
          <p className="whitespace-pre-wrap text-sm leading-7 text-slate-600 dark:text-gray-300">
            {announcement.message || "-"}
          </p>

          {hasCta && (
            <div className="mt-6 rounded-2xl border border-slate-200/80 bg-white/80 p-4 dark:border-gray-700 dark:bg-gray-800/80">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-slate-600 dark:text-gray-300">
                  เปิดรายละเอียดเพิ่มเติมของประกาศนี้
                </div>
                <button
                  type="button"
                  onClick={() => onNavigate(announcement.cta.url)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                >
                  {announcement.cta.label}
                  <FiExternalLink size={16} />
                </button>
              </div>
            </div>
          )}
        </div>

        {(dismissible || requiresAcknowledgement) && (
          <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-slate-200/80 px-4 py-4 dark:border-gray-700 sm:flex-row sm:justify-end sm:px-8 sm:py-5">
            {dismissible && (
              <button
                type="button"
                onClick={onDismiss}
                disabled={loading}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
              >
                ปิดประกาศ
              </button>
            )}

            {requiresAcknowledgement && (
              <button
                type="button"
                onClick={onAcknowledge}
                disabled={loading}
                className={`inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${style.button}`}
              >
                {loading ? "กำลังบันทึก..." : "รับทราบ"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementPopup;
